import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment-timezone';
import { useMutation } from '@apollo/client';
import { UserContext } from '../../../../context';
import { FollowMutation, UnfollowMutation, USER_FOLLOW, USER_UNFOLLOW } from '../../../../graphql/mutations';
import { User } from '../../../../common/core-api';
import { useFile } from '../../../../hooks';
import * as Styled from './UserItem.styled';

interface UserItemProps {
  user: User & { isFollowing?: boolean };
  onProfilePopupLoaded: (id: string) => void;
  type: 'followersTab' | 'followingTab';
  toggleFollowState: (id: string, type: string) => void;
  refetchUsers: () => void;
  isProfileOwner?: boolean;
  updateIndex: () => void;
}

const UserItem = ({
  user,
  onProfilePopupLoaded,
  type,
  toggleFollowState,
  refetchUsers,
  isProfileOwner,
  updateIndex
}: UserItemProps) => {
  const [getFile] = useFile();
  const currentUser = useContext(UserContext);

  const [followUser, { loading: userFollowLoading }] = useMutation<FollowMutation>(USER_FOLLOW);
  const [unfollowUser, { loading: userUnfollowLoading }] = useMutation<UnfollowMutation>(USER_UNFOLLOW);
  const url = user.profileImageFile && getFile({ file: user.profileImageFile, width: 100, height: 100 });
  const joinedDate = moment(user.createdAt).format('LL');
  const toggleFollow = async (id: string) => {
    updateIndex();

    const params = {
      variables: {
        byUser: currentUser.id,
        targetUser: id
      }
    };

    await (user.isFollowing ? unfollowUser(params) : followUser(params));

    refetchUsers();

    toggleFollowState(id, type);
  };

  return (
    <Styled.User>
      {url ? (
        <Styled.UserImage data-testid="UserProfileImage" src={url} onClick={() => onProfilePopupLoaded(user.id)} />
      ) : (
        <Styled.NoUserImage onClick={() => onProfilePopupLoaded(user.id)} />
      )}
      <Styled.UserDescr>
        <Styled.UserInfo>
          <Styled.UserName onClick={() => onProfilePopupLoaded(user.id)}>{user.displayName}</Styled.UserName>
          <Styled.Date>
            <FormattedMessage id="sinceDate" defaultMessage="User since {date}" values={{ date: joinedDate }} />
          </Styled.Date>
        </Styled.UserInfo>
        {isProfileOwner ? (
          <Styled.FollowBtn
            onClick={() => toggleFollow(user.id)}
            isLoading={userFollowLoading || userUnfollowLoading}
            priority="secondary"
            size="medium"
          >
            {user.isFollowing ? (
              <FormattedMessage id="unfollow" defaultMessage="Unfollow" />
            ) : (
              <FormattedMessage id="follow" defaultMessage="Follow" />
            )}
          </Styled.FollowBtn>
        ) : (
          ''
        )}
      </Styled.UserDescr>
    </Styled.User>
  );
};

export default UserItem;
